<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { DataTable } from "@/components/episto-ui/data-table";
  import { ProgressBar } from "@/components/episto-ui/progress-bar";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useSurveyProfilesStore } from "@stores/survey/surveyProfiles";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";

  const props = defineProps({ report: { type: Object, default: null } });
  const emit = defineEmits(["edit"]);

  const { surveyProfiles, locked } = storeToRefs(useSurveyProfilesStore());
  const { findCardByToken } = useSurveyCardsStore();
  const { localizeText } = useSurveyLocalesStore();

  const columns = [
    { key: "name", label: "Profile" },
    { key: "respondents", label: "Respondents", width: 110 },
    { key: "outcome", label: "Outcome", width: 220 },
    { key: "questions", label: "Questions", width: 100 },
    { key: "loi", label: "LOI", width: 80 },
  ];

  const outcomes = computed(() => Object.fromEntries((props.report?.profiles ?? []).map((row) => [row.token, row])));

  const isAi = (profile) => profile.token?.startsWith("ai-");

  const rulesSummary = (profile) => {
    const rules = (profile.question_params ?? []).map((rule) => {
      const card = findCardByToken({ token: rule.card_token });
      const answers = (rule.answers ?? [])
        .map((token) => localizeText(card?.specs?.answers?.find((answer) => answer.token === token)?.title) || token)
        .join(", ");
      return `${card?.name ?? rule.card_token} ${rule.type === "is_not" ? "≠" : "="} ${answers}`;
    });
    return [profile.country, ...rules].filter(Boolean).join(" · ") || "No forced answer";
  };

  const share = (count, total) => `${total ? (count / total) * 100 : 0}%`;

  const outcomeText = (row) =>
    [
      `${row.completes} completed`,
      row.screened_out && `${row.screened_out} screened out`,
      row.stopped && `${row.stopped} stopped`,
    ]
      .filter(Boolean)
      .join(" · ");
</script>

<template>
  <DataTable
    :items="surveyProfiles"
    :columns="columns"
    item-key="token"
    variant="dense"
    table-class="[&_th]:h-10! [&_th]:py-0!"
    fixed-widths
    :clickable-rows="!locked"
    empty-message="No profile yet: build them from the questionnaire above."
    @row-click="(profile) => !locked && emit('edit', surveyProfiles.indexOf(profile))">
    <template #cell-name="{ item }">
      <div class="flex min-w-0 flex-col gap-0.5">
        <span class="flex min-w-0 items-center gap-1.5 font-medium">
          <Tooltip
            v-if="isAi(item)"
            content="Created by Episto AI">
            <AiStar class="size-4 shrink-0 text-status-info-strong" />
          </Tooltip>
          <span class="truncate">{{ item.name }}</span>
        </span>
        <span class="truncate text-xs text-muted-foreground">{{ rulesSummary(item) }}</span>
      </div>
    </template>
    <template #cell-respondents="{ item }">
      <span class="tabular-nums">{{ item.n_current }}/{{ item.n_target }}</span>
    </template>
    <template #cell-outcome="{ item }">
      <ProgressBar
        v-if="locked || !outcomes[item.token]"
        :percentage="item.n_target ? (item.n_current / item.n_target) * 100 : 0"
        size="sm" />
      <div
        v-else
        class="flex flex-col gap-1">
        <div class="flex h-1.5 overflow-hidden rounded-full bg-muted">
          <span
            class="bg-status-success"
            :style="{ width: share(outcomes[item.token].completes, outcomes[item.token].respondents) }" />
          <span
            class="bg-status-error"
            :style="{ width: share(outcomes[item.token].screened_out, outcomes[item.token].respondents) }" />
          <span
            class="bg-status-warning"
            :style="{ width: share(outcomes[item.token].stopped, outcomes[item.token].respondents) }" />
        </div>
        <span class="text-xs text-muted-foreground">{{ outcomeText(outcomes[item.token]) }}</span>
      </div>
    </template>
    <template #cell-questions="{ item }">
      <span class="tabular-nums">{{ item.answered_questions?.avg || "—" }}</span>
    </template>
    <template #cell-loi="{ item }">
      <span class="tabular-nums">{{ item.loi_prediction?.avg ? `${item.loi_prediction.avg} min` : "—" }}</span>
    </template>
  </DataTable>
</template>
